import { Stack, Text } from "@certn/cascadia";
import { splitLabelHeader } from "@certn/api";
import { Schemas } from "../../types";
import { Grid, GridItem, List, ListItem } from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";
import { ReactNode } from "react";

/**
 * Structural shape shared by Custom Questionnaire and Fitness & Propriety
 * template detail questions. Both surface the same QuestionnaireQuestion
 * model behind the scenes; their generated TS types differ only in name
 * prefix, so we accept either via a structural alias and reuse this preview
 * component across both settings pages.
 */
export type QuestionPreviewShape = Pick<
  Schemas["CustomQuestionnaireTemplateDetailQuestions"],
  | "id"
  | "external_name"
  | "data_type"
  | "is_optional"
  | "selectable_options"
  | "display_conditions"
>;

type CustomTemplateResponsePreviewProps = QuestionPreviewShape & {
  /**
   * When true, the question is being rendered as a follow-up nested directly
   * inside its parent's collapsible body (see ``CustomTemplateQuestionPreview``).
   * In that layout the parent's question text is already on screen immediately
   * above, so the "Shown when" row condenses to just the condition value
   * (e.g. "answer = Yes") instead of repeating the parent text.
   */
  isNestedFollowUp?: boolean;
};

export const CustomTemplateResponsePreview = ({
  data_type,
  external_name,
  selectable_options,
  is_optional,
  display_conditions,
  isNestedFollowUp = false,
}: CustomTemplateResponsePreviewProps) => {
  const { t } = useTranslation();

  const RESPONSE_TYPE_BY_DATA_TYPE: Record<string, string> = {
    bool: t("Yes/No"),
    int: t("Number"),
    float: t("Number"),
    file_upload: t("Document Upload"),
  };

  const hasConditions = display_conditions.length > 0;
  const hasSelectableOptions =
    selectable_options &&
    Array.isArray(selectable_options) &&
    selectable_options.length > 0;
  const { header, fieldLabel } = splitLabelHeader(external_name);

  const getResponseType = (
    type: Schemas["CustomQuestionnaireTemplateDetailQuestions"]["data_type"]
  ) => {
    const text = t("Text");

    if (type === "str") {
      return hasSelectableOptions ? t("Multiple choice") : text;
    }

    return RESPONSE_TYPE_BY_DATA_TYPE[type] ?? text;
  };

  return (
    <Stack gap="space-4">
      {is_optional && (
        <CustomTemplateResponseSectionPreview
          label={t("Is optional")}
          content={t("Yes")}
        />
      )}
      {hasConditions && (
        <CustomTemplateResponseSectionPreview
          label={t("Shown when")}
          content={
            <Stack>
              {display_conditions.map((condition) => {
                const condition_value = condition.condition_value
                  ? t("Yes")
                  : t("No");
                const text = isNestedFollowUp
                  ? `${t("answer")} = ${condition_value}`
                  : `${condition.parent_question.external_name} = ${condition_value}`;

                return (
                  <Text key={condition.id} style="global/body-primary">
                    {text}
                  </Text>
                );
              })}
            </Stack>
          }
        />
      )}
      <CustomTemplateResponseSectionPreview
        label={t("Response type")}
        content={getResponseType(data_type)}
      />
      {header && (
        <CustomTemplateResponseSectionPreview
          label={t("Section header")}
          content={header}
        />
      )}
      <CustomTemplateResponseSectionPreview
        label={t("Question")}
        content={fieldLabel}
      />
      {hasSelectableOptions && (
        <CustomTemplateResponseSectionPreview
          label={t("Options")}
          content={
            <List type="unordered">
              {selectable_options.map((option, index) => (
                <ListItem key={index}>
                  <Text style="global/body-primary">{option.label}</Text>
                </ListItem>
              ))}
            </List>
          }
        />
      )}
    </Stack>
  );
};

const CustomTemplateResponseSectionPreview = ({
  label,
  content,
}: {
  label: string;
  content: ReactNode;
}) => {
  return (
    <Grid>
      <GridItem span={4}>
        <Text style="card/title-medium">{label}</Text>
      </GridItem>
      <GridItem span={8}>
        {typeof content === "string" ? (
          <Text style="global/body-primary">{content}</Text>
        ) : (
          content
        )}
      </GridItem>
    </Grid>
  );
};
